// ./src/utils/api/UserService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem, UserAccount } from "./commonApiTypes";
import { UserRole } from "./UserRoleService";
import { Account } from "./AccountService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const USER_FIELDS = `
  id
  username
  email
  emailConfirmation
  uiLanguage
  timeZone
  firstName
  middleName
  lastName
  avatarUrl
  birthday
  status
  userAccounts {
    accountId
    userRoleId
    userRole {
      roleName
    }
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const USER_LIST_QUERY = gql`
  query UserList($filter: UserFilter, $params: PaginationAndSorting) {
    userList(filter: $filter, params: $params) {
      code
      data {
        ${USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_GET_QUERY = gql`
  query UserGet($id: ID!) {
    userGet(id: $id) {
      code
      data {
        ${USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_GET_MANY_QUERY = gql`
  query UserGetMany($ids: [ID]!) {
    userGetMany(ids: $ids) {
      code
      data {
        ${USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const USER_CREATE_MUTATION = gql`
  mutation UserCreate($params: UserInput!) {
    userCreate(params: $params) {
      code
      data {
        ${USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_CREATE_MANY_MUTATION = gql`
  mutation UserCreateMany($params: [UserInput]!) {
    userCreateMany(params: $params) {
      code
      data {
        ${USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_UPDATE_MUTATION = gql`
  mutation UserUpdate($where: UserWhereInput!, $params: UserInput!) {
    userUpdate(where: $where, params: $params) {
      code
      data {
        ${USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_UPDATE_MANY_MUTATION = gql`
  mutation UserUpdateMany($params: [UserUpdateManyInput]!) {
    userUpdateMany(params: $params) {
      code
      data {
        ${USER_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_REMOVE_MUTATION = gql`
  mutation UserRemove($where: UserWhereInput!) {
    userRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const USER_REMOVE_MANY_MUTATION = gql`
  mutation UserRemoveMany($where: [UserWhereInput]!) {
    userRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface User {
  id?: string | null | undefined;
  username?: string | null | undefined;
  email?: string | null | undefined;
  emailConfirmation?: boolean | null | undefined;
  uiLanguage?: string | null | undefined;
  timeZone?: string | null | undefined;
  firstName?: string | null | undefined;
  middleName?: string | null | undefined;
  lastName?: string | null | undefined;
  avatarUrl?: string | null | undefined;
  birthday?: string | null | undefined;
  status?: number | null | undefined;
  token?: string | null | undefined;
  fingerprintToken?: string | null | undefined;
  refreshToken?: string | null | undefined;
  userAccounts?: UserAccount[] | null | undefined;
  userRole?: UserRole | null | undefined;
  account?: Account | null | undefined;
}

export interface UserInput {
  email?: string | null | undefined;
  uiLanguage?: string | null | undefined;
  timeZone?: string | null | undefined;
  firstName?: string | null | undefined;
  middleName?: string | null | undefined;
  lastName?: string | null | undefined;
  avatarUrl?: string | null | undefined;
  birthday?: string | null | undefined;
  password?: string | null | undefined;
  repeatPassword?: string | null | undefined;
  status?: number | null | undefined;
}

export interface UserFilter {
  id?: string[] | null | undefined;
  accountId?: string | null | undefined;
  searchIn?: string[] | null | undefined;
  keyword?: string | null | undefined;
}

export interface UserWhereInput {
  id?: string | null | undefined;
  username?: string | null | undefined;
}

export interface UserUpdateManyInput {
  where: UserWhereInput;
  params: UserInput;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * User service that handles all user-related API calls
 * Supports full CRUD operations: list, get, getMany, create, createMany, update, updateMany, remove, removeMany
 */
export class UserService {
  /**
   * Get a list of users with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: UserFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(USER_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single user by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(USER_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple users by their IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(USER_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Create a new user
   */
  static async create(params: UserInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(USER_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Create multiple users
   */
  static async createMany(params: UserInput[]): Promise<OpResult> {
    const result = await ApiService.executeMutation(USER_CREATE_MANY_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing user
   */
  static async update(
    where: UserWhereInput,
    params: UserInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(USER_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Update multiple users
   */
  static async updateMany(params: UserUpdateManyInput[]): Promise<OpResult> {
    const result = await ApiService.executeMutation(USER_UPDATE_MANY_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Remove/delete a user
   */
  static async remove(where: UserWhereInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(USER_REMOVE_MUTATION, {
      where,
    });
    return result;
  }

  /**
   * Remove/delete multiple users
   */
  static async removeMany(where: UserWhereInput[]): Promise<OpResult> {
    const result = await ApiService.executeMutation(USER_REMOVE_MANY_MUTATION, {
      where,
    });
    return result;
  }
}